import { useCallback, useMemo, useState } from 'react';
type CookieOptions = {
  path?: string;          // default: "/"
  domain?: string;
  maxAge?: number;        // seconds
  expires?: Date;         // if provided, overrides maxAge
  secure?: boolean;
  sameSite?: 'lax' | 'strict' | 'none';
};

function getCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([$()*+./?[\\\]^{|}])/g, '\\$1') + '=([^;]*)')
  );

  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, options: CookieOptions = {}) {
  const {
    path = '/',
    domain,
    maxAge,
    expires,
    secure,
    sameSite = 'lax',
  } = options;

  let cookie = `${name}=${encodeURIComponent(value)}`;

  if (expires) cookie += `; Expires=${expires.toUTCString()}`;
  if (typeof maxAge === 'number') cookie += `; Max-Age=${Math.floor(maxAge)}`;
  if (domain) cookie += `; Domain=${domain}`;
  if (path) cookie += `; Path=${path}`;
  if (secure) cookie += `; Secure`;
  if (sameSite) cookie += `; SameSite=${sameSite}`;

  document.cookie = cookie;
}

function removeCookie(name: string, options: Pick<CookieOptions, 'path' | 'domain'> = {}) {
  setCookie(name, '', {
    ...options,
    expires: new Date(0), // epoch
  });
}

export function useCookie(name: string) {
  const [value, setValueState] = useState<string | null>(() => getCookie(name));

  const refresh = useCallback(() => {
    setValueState(getCookie(name));
  }, [name]);

  const setValue = useCallback(
    (newValue: string, options?: CookieOptions) => {
      setCookie(name, newValue, options);
      setValueState(newValue);
    },
    [name]
  );

  const remove = useCallback(
    (options?: Pick<CookieOptions, 'path' | 'domain'>) => {
      removeCookie(name, options ?? {});
      setValueState(null);
    },
    [name]
  );

  return useMemo(
    () => ({ value, setCookie: setValue, removeCookie: remove, refresh }),
    [value, setValue, remove]
  );
}

export default useCookie;